import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    requestPinyaCopilotPrecompute,
    requestPinyaCopilotSuggestion,
} from './copilotApi';
import {
    trackCopilotSessionShown,
    trackCopilotSessionDecision,
} from './copilotMonitoringApi';
import {
    buildCopilotContextKey,
    buildCopilotLocalSnapshotKey,
    isCaixaEmpty,
    pickBestSuggestionFromList,
} from './copilotSelectors';
import { COPILOT_STATUS } from './copilotTypes';

const MAX_RANKING_CACHE_ENTRIES = 120;

const createSessionId = () => {
    if (typeof window?.crypto?.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }

    return `copilot-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

function usePinyaCopilot({
    readonly,
    selectedEvent,
    selectedBundle,
    selectedVersio,
    pestanya,
    caixaSelected,
    posicions,
    assistenciesEvent,
    setCastellerSelected,
    setNextChangeFromSuggestion,
    visibleEmptyCaixaIds,
    withAddons,
    retryToken,
}) {
    const [copilotSuggestion, setCopilotSuggestion] = useState(null);
    const [copilotStatus, setCopilotStatus] = useState(COPILOT_STATUS.IDLE);
    const [rankingState, setRankingState] = useState(null);

    const activeRequestIdRef = useRef(0);
    const activeAbortControllerRef = useRef(null);
    const rankingCacheRef = useRef(new Map());
    const lastPrecomputeScopeRef = useRef(null);
    const activeMonitoringSessionRef = useRef(null);

    const resolveActiveMonitoringSession = useCallback(({ decisionType, decisionSource, decisionReason = null }) => {
        const activeSession = activeMonitoringSessionRef.current;
        if (!activeSession || activeSession.resolved) return;

        activeSession.resolved = true;
        trackCopilotSessionDecision({
            sessionId: activeSession.sessionId,
            decisionType,
            decisionSource,
            decisionReason,
        }).catch(() => null);
    }, []);

    const clearSuggestionAndResolveImplicit = useCallback((decisionReason, nextStatus = COPILOT_STATUS.IDLE) => {
        resolveActiveMonitoringSession({
            decisionType: 'ignored_implicit',
            decisionSource: 'system',
            decisionReason,
        });

        setCopilotSuggestion(null);
        setCopilotStatus(nextStatus);
    }, [resolveActiveMonitoringSession]);

    const clearSuggestionStateOnly = useCallback(() => {
        setCopilotSuggestion(null);
        setCopilotStatus(COPILOT_STATUS.IDLE);
    }, []);

    const contextInfo = useMemo(() => {
        const eventId = parseInt(selectedEvent, 10);
        const bundleId = String(selectedBundle || '').trim();
        const versio = String(selectedVersio || '').trim();
        const normalizedPestanya = String(pestanya || '').trim().toLowerCase();

        if (Number.isNaN(eventId) || !bundleId || !versio || !normalizedPestanya) {
            return null;
        }

        const contextKey = buildCopilotContextKey({
            eventId,
            bundleId,
            versio,
            pestanya: normalizedPestanya,
        });

        return {
            eventId,
            bundleId,
            versio,
            pestanya: normalizedPestanya,
            contextKey,
        };
    }, [selectedEvent, selectedBundle, selectedVersio, pestanya]);

    const posicionsCaixesSnapshot = JSON.stringify(posicions?.caixes || {});
    const posicionsCastellersSnapshot = JSON.stringify(posicions?.castellers || {});
    const assistenciesSnapshot = JSON.stringify(assistenciesEvent || []);

    const localSnapshotKey = useMemo(() => buildCopilotLocalSnapshotKey({
        posicions,
        assistenciesEvent,
    }), [
        posicionsCaixesSnapshot,
        posicionsCastellersSnapshot,
        assistenciesSnapshot,
    ]);

    const visibleEmptySnapshot = JSON.stringify(visibleEmptyCaixaIds || []);
    const normalizedVisibleEmptyCaixaIds = useMemo(
        () => [...new Set(
            (visibleEmptyCaixaIds || [])
                .map(caixaId => String(caixaId || '').trim())
                .filter(Boolean)
        )].sort((a, b) => a.localeCompare(b)),
        [visibleEmptySnapshot]
    );
    const visibleEmptyCaixaIdsKey = JSON.stringify(normalizedVisibleEmptyCaixaIds);
    const visibleEmptyCaixaSet = useMemo(
        () => new Set(normalizedVisibleEmptyCaixaIds),
        [visibleEmptyCaixaIdsKey]
    );

    useEffect(() => {
        if (readonly || !contextInfo || contextInfo.pestanya !== 'pinya') {
            lastPrecomputeScopeRef.current = null;
            return;
        }

        const precomputeScopeKey = `${contextInfo.contextKey}::${localSnapshotKey}::${visibleEmptyCaixaIdsKey}`;
        if (lastPrecomputeScopeRef.current === precomputeScopeKey) {
            return;
        }

        lastPrecomputeScopeRef.current = precomputeScopeKey;
        requestPinyaCopilotPrecompute({
            eventId: contextInfo.eventId,
            bundleId: contextInfo.bundleId,
            versio: contextInfo.versio,
            pestanya: contextInfo.pestanya,
            visibleCaixaIds: normalizedVisibleEmptyCaixaIds,
            stateHash: localSnapshotKey,
        }).catch(() => null);
    }, [
        readonly,
        contextInfo?.contextKey,
        localSnapshotKey,
        visibleEmptyCaixaIdsKey,
    ]);

    useEffect(() => {
        if (readonly || !contextInfo || contextInfo.pestanya !== 'pinya') {
            if (activeAbortControllerRef.current) {
                activeAbortControllerRef.current.abort();
                activeAbortControllerRef.current = null;
            }

            setRankingState(null);
            clearSuggestionAndResolveImplicit('readonly_or_missing_context');
            return;
        }

        if (caixaSelected === -1 || !isCaixaEmpty(posicions, caixaSelected)) {
            if (activeAbortControllerRef.current) {
                activeAbortControllerRef.current.abort();
                activeAbortControllerRef.current = null;
            }

            setRankingState(null);
            clearSuggestionAndResolveImplicit('caixa_not_empty_or_unselected');
            return;
        }

        const selectedCaixaId = String(caixaSelected);
        if (!visibleEmptyCaixaSet.has(selectedCaixaId)) {
            if (activeAbortControllerRef.current) {
                activeAbortControllerRef.current.abort();
                activeAbortControllerRef.current = null;
            }

            setRankingState(null);
            clearSuggestionAndResolveImplicit('caixa_not_visible');
            return;
        }

        const requestKey = `${contextInfo.contextKey}.${caixaSelected}`;
        if (
            rankingState?.requestKey === requestKey &&
            rankingState?.localSnapshotKey === localSnapshotKey
        ) {
            return;
        }

        const cachedRanking = rankingCacheRef.current.get(requestKey);
        if (
            cachedRanking &&
            cachedRanking.localSnapshotKey === localSnapshotKey
        ) {
            setRankingState(cachedRanking);
            setCopilotStatus(COPILOT_STATUS.IDLE);
            return;
        }

        if (activeAbortControllerRef.current) {
            activeAbortControllerRef.current.abort();
            activeAbortControllerRef.current = null;
        }

        const requestId = activeRequestIdRef.current + 1;
        activeRequestIdRef.current = requestId;

        const controller = new AbortController();
        activeAbortControllerRef.current = controller;

        resolveActiveMonitoringSession({
            decisionType: 'ignored_implicit',
            decisionSource: 'system',
            decisionReason: 'suggestion_reloading',
        });
        activeMonitoringSessionRef.current = null;
        setCopilotSuggestion(null);
        setCopilotStatus(COPILOT_STATUS.LOADING);

        const payload = {
            eventId: contextInfo.eventId,
            bundleId: contextInfo.bundleId,
            versio: contextInfo.versio,
            caixaId: String(caixaSelected),
            pestanya: contextInfo.pestanya,
            visibleCaixaIds: normalizedVisibleEmptyCaixaIds,
            stateHash: localSnapshotKey,
        };

        requestPinyaCopilotSuggestion(payload, { signal: controller.signal })
            .then(response => {
                if (activeRequestIdRef.current !== requestId) return;
                if (activeAbortControllerRef.current === controller) {
                    activeAbortControllerRef.current = null;
                }

                if (!response?.enabled) {
                    setRankingState(null);
                    clearSuggestionAndResolveImplicit('copilot_disabled', COPILOT_STATUS.DISABLED);
                    return;
                }

                if (response?.status === 'error') {
                    setRankingState(null);
                    clearSuggestionAndResolveImplicit('suggestion_error', COPILOT_STATUS.ERROR);
                    return;
                }

                const nextRankingState = {
                    requestKey,
                    caixaId: String(caixaSelected),
                    rankedCastellerIds: Array.isArray(response?.rankedCastellerIds)
                        ? response.rankedCastellerIds
                        : [],
                    provider: response?.provider || null,
                    model: response?.model || null,
                    latencyMs: response?.latencyMs ?? null,
                    contextHash: response?.contextHash || null,
                    cached: Boolean(response?.cached),
                    localSnapshotKey,
                };

                if (rankingCacheRef.current.has(requestKey)) {
                    rankingCacheRef.current.delete(requestKey);
                }
                rankingCacheRef.current.set(requestKey, nextRankingState);
                while (rankingCacheRef.current.size > MAX_RANKING_CACHE_ENTRIES) {
                    const oldestKey = rankingCacheRef.current.keys().next().value;
                    rankingCacheRef.current.delete(oldestKey);
                }

                setRankingState(nextRankingState);

                setCopilotStatus(COPILOT_STATUS.IDLE);
            })
            .catch(error => {
                if (error?.name === 'AbortError') return;
                if (activeRequestIdRef.current !== requestId) return;
                if (activeAbortControllerRef.current === controller) {
                    activeAbortControllerRef.current = null;
                }

                setRankingState(null);
                clearSuggestionAndResolveImplicit('suggestion_request_failed', COPILOT_STATUS.ERROR);
            });

        return () => {
            if (activeAbortControllerRef.current === controller) {
                controller.abort();
                activeAbortControllerRef.current = null;
            }
        };
    }, [
        readonly,
        contextInfo?.contextKey,
        caixaSelected,
        localSnapshotKey,
        visibleEmptyCaixaIdsKey,
        retryToken,
        clearSuggestionAndResolveImplicit,
        resolveActiveMonitoringSession,
    ]);

    useEffect(() => {
        if (readonly || !contextInfo || contextInfo.pestanya !== 'pinya') {
            clearSuggestionAndResolveImplicit('readonly_or_missing_context');
            return;
        }

        if (!rankingState) {
            clearSuggestionAndResolveImplicit('ranking_unavailable');
            return;
        }

        if (caixaSelected === -1 || String(rankingState.caixaId) !== String(caixaSelected)) {
            clearSuggestionAndResolveImplicit('caixa_changed');
            return;
        }

        if (!isCaixaEmpty(posicions, caixaSelected)) {
            clearSuggestionAndResolveImplicit('caixa_filled');
            return;
        }

        const bestCastellerId = pickBestSuggestionFromList({
            rankedCastellerIds: rankingState.rankedCastellerIds,
            posicions,
            assistenciesEvent,
        });

        if (!bestCastellerId) {
            clearSuggestionAndResolveImplicit('no_candidate_after_recheck');
            return;
        }

        const nextSuggestion = {
            caixaId: String(caixaSelected),
            suggestedCastellerId: bestCastellerId,
            requestKey: rankingState.requestKey,
            provider: rankingState.provider,
            model: rankingState.model,
            latencyMs: rankingState.latencyMs,
            contextHash: rankingState.contextHash,
            cached: rankingState.cached,
        };

        const alreadySame =
            copilotSuggestion &&
            String(copilotSuggestion.caixaId) === String(nextSuggestion.caixaId) &&
            parseInt(copilotSuggestion.suggestedCastellerId, 10) === parseInt(nextSuggestion.suggestedCastellerId, 10);

        if (!alreadySame) {
            resolveActiveMonitoringSession({
                decisionType: 'ignored_implicit',
                decisionSource: 'system',
                decisionReason: 'suggestion_replaced',
            });

            const sessionId = createSessionId();
            const caixaMetadata = withAddons?.[String(caixaSelected)] || withAddons?.[caixaSelected] || null;
            const suggestionWithSession = {
                ...nextSuggestion,
                sessionId,
            };

            activeMonitoringSessionRef.current = {
                sessionId,
                resolved: false,
            };

            trackCopilotSessionShown({
                sessionId,
                eventId: contextInfo.eventId,
                bundleId: contextInfo.bundleId,
                versio: contextInfo.versio,
                pestanya: contextInfo.pestanya,
                caixaId: String(caixaSelected),
                caixaPerfil: caixaMetadata?.perfil || null,
                caixaEtiqueta: caixaMetadata?.etiqueta || null,
                suggestedCastellerId: bestCastellerId,
                rankingSize: Array.isArray(rankingState?.rankedCastellerIds)
                    ? rankingState.rankedCastellerIds.length
                    : null,
                provider: rankingState?.provider || null,
                model: rankingState?.model || null,
                    latencyMs: rankingState?.latencyMs ?? null,
                cached: Boolean(rankingState?.cached),
                contextHash: rankingState?.contextHash || null,
            }).catch(() => null);

            setCopilotSuggestion(suggestionWithSession);
        }

        setCopilotStatus(COPILOT_STATUS.READY);
    }, [
        readonly,
        contextInfo?.contextKey,
        caixaSelected,
        rankingState?.requestKey,
        rankingState?.localSnapshotKey,
        JSON.stringify(rankingState?.rankedCastellerIds || []),
        localSnapshotKey,
        JSON.stringify(withAddons || {}),
        clearSuggestionAndResolveImplicit,
        resolveActiveMonitoringSession,
    ]);

    const dismissCopilotSuggestion = useCallback((source = 'desktop_button') => {
        resolveActiveMonitoringSession({
            decisionType: 'ignored_explicit',
            decisionSource: source,
            decisionReason: 'user_dismissed',
        });
        activeMonitoringSessionRef.current = null;
        clearSuggestionStateOnly();
    }, [clearSuggestionStateOnly, resolveActiveMonitoringSession]);

    const acceptCopilotSuggestion = useCallback((source = 'system') => {
        if (!copilotSuggestion || !rankingState) return false;

        const bestNow = pickBestSuggestionFromList({
            rankedCastellerIds: rankingState.rankedCastellerIds,
            posicions,
            assistenciesEvent,
        });

        if (!bestNow) {
            clearSuggestionAndResolveImplicit('no_candidate_on_accept');
            return false;
        }

        setNextChangeFromSuggestion(true);

        resolveActiveMonitoringSession({
            decisionType: 'accepted',
            decisionSource: source,
            decisionReason: null,
        });
        activeMonitoringSessionRef.current = null;
        setCastellerSelected(bestNow);
        clearSuggestionStateOnly();
        return true;
    }, [
        copilotSuggestion,
        rankingState,
        posicions,
        assistenciesEvent,
        setCastellerSelected,
        setNextChangeFromSuggestion,
        clearSuggestionAndResolveImplicit,
        clearSuggestionStateOnly,
        resolveActiveMonitoringSession,
    ]);

    useEffect(() => () => {
        resolveActiveMonitoringSession({
            decisionType: 'ignored_implicit',
            decisionSource: 'system',
            decisionReason: 'hook_unmount',
        });
    }, [resolveActiveMonitoringSession]);

    return {
        copilotStatus,
        copilotSuggestion,
        acceptCopilotSuggestion,
        dismissCopilotSuggestion,
    };
}

export default usePinyaCopilot;
